'use client'

import React from 'react'
import { RiArrowUpSLine, RiArrowDownSLine, RiLock2Line } from '@remixicon/react'
import { InfoHint } from './InfoHint'

export interface StatCardProps {
  label: string
  value: React.ReactNode
  /** Explains the figure when its name alone could be read as something else. */
  hint?: string
  /** Change against the previous period; omitted when the API sends none. */
  delta?: { value: string; positive: boolean }
  icon?: React.ReactNode
  loading?: boolean
  /**
   * Renders the tile as a placeholder for a metric that exists in the design but
   * has no data source yet. Co-owner had several of these written as
   * `false ? <real/> : <comingSoon/>`, which kept unreachable JSX in the tree.
   */
  locked?: { message: string }
  className?: string
}

/**
 * One headline number.
 *
 * Fixed internal structure — icon chip and label on top, value and delta on the
 * bottom, `justify-between` between them — so a row of these lines up whether or
 * not each has an icon, a hint or a delta.
 */
export const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  hint,
  delta,
  icon,
  loading = false,
  locked,
  className = '',
}) => (
  <div
    className={`bg-white/80 rounded-2xl p-6 h-full flex flex-col justify-between gap-4 ${className}`}
  >
    <div className="flex items-center gap-2">
      {icon && (
        <span className="w-8 h-8 rounded-full bg-dashboard-accent-tint flex items-center justify-center text-dashboard-accent shrink-0">
          {icon}
        </span>
      )}
      <span className="inline-flex items-center gap-1 text-xs font-medium text-dashboard-muted font-poppins">
        {label}
        {hint && <InfoHint label={hint} ariaLabel={`What is ${label}?`} />}
      </span>
    </div>

    {locked ? (
      <div className="flex items-center gap-1.5 text-dashboard-faint">
        <RiLock2Line size={14} className="shrink-0" />
        <span className="text-[13px] font-medium font-poppins">{locked.message}</span>
      </div>
    ) : loading ? (
      <div className="h-8 w-24 rounded bg-gray-100 animate-pulse" aria-hidden />
    ) : (
      <div className="flex items-end justify-between gap-2">
        <span className="text-2xl font-semibold text-dashboard-text font-poppins leading-8">
          {value}
        </span>
        {delta && (
          <span
            className={`flex items-center gap-0.5 text-xs font-semibold font-poppins ${
              delta.positive ? 'text-dashboard-positive' : 'text-dashboard-negative'
            }`}
          >
            {delta.positive ? <RiArrowUpSLine size={14} /> : <RiArrowDownSLine size={14} />}
            {delta.value}
          </span>
        )}
      </div>
    )}
  </div>
)

/**
 * The standard stat row: one column on a phone, two on a small tablet, four on a
 * wide screen. Two columns rather than four at `sm` because four 6-padded cards
 * below ~1280px leave no room for the value.
 */
export const StatCardGrid: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = '',
}) => (
  <div className={`grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 ${className}`}>
    {children}
  </div>
)
